import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getUserFollowers, getUserFollowing } from '../api/users';
import useAuth from '../hooks/useAuth';
import UserCard from '../components/user/UserCard';
import Loader from '../components/common/Loader';
import Button from '../components/common/Button';

/**
 * Followers/Following page component
 * Displays lists of followers and people the user is following
 * @returns {JSX.Element} Followers/Following page
 */
const Followers = () => {
  const { username, tab = 'followers' } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState(tab);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState({
    followers: false,
    following: false,
  });
  const [error, setError] = useState({
    followers: '',
    following: '',
  });
  const [page, setPage] = useState({
    followers: 1,
    following: 1,
  });
  const [hasMore, setHasMore] = useState({
    followers: false,
    following: false,
  });

  // Get userId based on username in URL or current user
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    // If username is "me", use current user's ID
    if (username === 'me' && user) {
      const currentUser = user.data?.user || user.user || user;
      setUserId(currentUser._id);
    } else if (user) {
      // Otherwise, need to fetch the user ID based on username
      // For simplicity, if the username matches the current user, use their ID
      const currentUser = user.data?.user || user.user || user;
      if (currentUser.username === username) {
        setUserId(currentUser._id);
      } else {
        // In a real app, you'd fetch the user by username here
        // For now, we'll assume we have the ID
        setUserId(username);
      }
    }
  }, [username, user]);

  // Update URL when tab changes
  useEffect(() => {
    if (activeTab !== tab) {
      navigate(`/profile/${username}/${activeTab}`, { replace: true });
    }
  }, [activeTab, username, navigate, tab]);

  // Fetch followers when userId is available or tab changes
  useEffect(() => {
    if (userId && activeTab === 'followers') {
      fetchFollowers(1);
    }
  }, [userId, activeTab]);

  // Fetch following when userId is available or tab changes
  useEffect(() => {
    if (userId && activeTab === 'following') {
      fetchFollowing(1);
    }
  }, [userId, activeTab]);

  // Handle follow/unfollow from UserCard component
  const handleFollowChange = (isNowFollowing, userId) => {
    // Update state for the "followers" tab
    if (activeTab === 'followers') {
      setFollowers(prevFollowers => 
        prevFollowers.map(follower => 
          follower._id === userId 
            ? { ...follower, isFollowing: isNowFollowing } 
            : follower
        )
      );
    }
    
    // Update state for the "following" tab
    // Remove users from the following list if they are unfollowed
    if (activeTab === 'following' && !isNowFollowing) {
      setFollowing(prevFollowing => 
        prevFollowing.filter(user => user._id !== userId)
      );
    } else if (activeTab === 'following') {
      setFollowing(prevFollowing => 
        prevFollowing.map(user => 
          user._id === userId 
            ? { ...user, isFollowing: isNowFollowing } 
            : user
        )
      );
    }
  };

  // Fetch followers
  const fetchFollowers = async (pageNum) => {
    if (!userId) return;

    try {
      setLoading(prev => ({ ...prev, followers: true }));
      setError(prev => ({ ...prev, followers: '' }));

      const response = await getUserFollowers(userId, {
        page: pageNum,
        limit: 20,
      });

      // Handle the specific response structure
      console.log("Followers response:", response.data);
      
      // Extract followers from data.followers
      let newFollowers = response.data.data?.followers || [];
      
      // Check if the followers include isFollowing data
      // If not, we need to determine whether the current user is following each follower
      if (newFollowers.length > 0 && user) {
        const currentUserData = user.data?.user || user.user || user;
        const currentUserFollowing = currentUserData.following || [];
        
        // Mark followers that the current user is following
        newFollowers = newFollowers.map(follower => ({
          ...follower,
          isFollowing: currentUserFollowing.includes(follower._id) || 
                       response.data.data?.followingMap?.[follower._id] || 
                       false
        }));
      }
      
      const pagination = response.data.pagination || { 
        page: pageNum, 
        pages: Math.ceil((response.data.total || 0) / 20)
      };

      if (pageNum === 1) {
        setFollowers(newFollowers);
      } else {
        setFollowers(prev => [...prev, ...newFollowers]);
      }

      setPage(prev => ({ ...prev, followers: pagination.page }));
      setHasMore(prev => ({ ...prev, followers: pagination.page < pagination.pages }));
    } catch (err) {
      console.error('Error fetching followers:', err);
      setError(prev => ({ 
        ...prev, 
        followers: 'Failed to load followers. Please try again.' 
      }));
    } finally {
      setLoading(prev => ({ ...prev, followers: false }));
    }
  };

  // Fetch following
  const fetchFollowing = async (pageNum) => {
    if (!userId) return;

    try {
      setLoading(prev => ({ ...prev, following: true }));
      setError(prev => ({ ...prev, following: '' }));

      const response = await getUserFollowing(userId, {
        page: pageNum,
        limit: 20,
      });

      // Handle the specific response structure
      console.log("Following response:", response.data);
      
      // Extract following from data.following and mark them as being followed
      const newFollowing = (response.data.data?.following || []).map(user => ({
        ...user,
        isFollowing: true // Mark all users in the following list as already being followed
      }));
      
      const pagination = response.data.pagination || { 
        page: pageNum, 
        pages: Math.ceil((response.data.total || 0) / 20)
      };

      if (pageNum === 1) {
        setFollowing(newFollowing);
      } else {
        setFollowing(prev => [...prev, ...newFollowing]);
      }

      setPage(prev => ({ ...prev, following: pagination.page }));
      setHasMore(prev => ({ ...prev, following: pagination.page < pagination.pages }));
    } catch (err) {
      console.error('Error fetching following:', err);
      setError(prev => ({ 
        ...prev, 
        following: 'Failed to load following. Please try again.' 
      }));
    } finally {
      setLoading(prev => ({ ...prev, following: false }));
    }
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Load more users
  const loadMore = () => {
    if (activeTab === 'followers' && hasMore.followers && !loading.followers) {
      fetchFollowers(page.followers + 1);
    } else if (activeTab === 'following' && hasMore.following && !loading.following) {
      fetchFollowing(page.following + 1);
    }
  };

  // Render
  if (authLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center py-12">
          <Loader size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <div className="sm:flex sm:items-center px-6 py-3">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Connections</h3>
              <div className="mt-3 sm:mt-0 sm:ml-4">
                <div className="flex space-x-4">
                  <button
                    className={`px-3 py-2 font-medium text-sm rounded-md ${
                      activeTab === 'followers'
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => handleTabChange('followers')}
                  >
                    Followers
                  </button>
                  <button
                    className={`px-3 py-2 font-medium text-sm rounded-md ${
                      activeTab === 'following'
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => handleTabChange('following')}
                  >
                    Following
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {/* Followers tab */}
            {activeTab === 'followers' && (
              <div>
                {error.followers && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                    {error.followers}
                  </div>
                )}

                {loading.followers && followers.length === 0 ? (
                  <div className="flex justify-center py-12">
                    <Loader size="lg" />
                  </div>
                ) : followers.length === 0 ? (
                  <div className="text-center py-12">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                    <h3 className="mt-2 text-lg font-medium text-gray-900">No followers yet</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      When people follow you, they'll appear here.
                    </p>
                  </div>
                ) : (
                  <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {followers.map((follower) => (
                        <UserCard 
                          key={follower._id} 
                          user={follower} 
                          showFollowButton 
                          showMessageButton={true}
                          onFollowChange={handleFollowChange}
                        />
                      ))}
                    </div>

                    {hasMore.followers && (
                      <div className="mt-6 flex justify-center">
                        <Button onClick={loadMore} variant="light" isLoading={loading.followers}>
                          Load More
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Following tab */}
            {activeTab === 'following' && (
              <div>
                {error.following && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                    {error.following}
                  </div>
                )}

                {loading.following && following.length === 0 ? (
                  <div className="flex justify-center py-12">
                    <Loader size="lg" />
                  </div>
                ) : following.length === 0 ? (
                  <div className="text-center py-12">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                    <h3 className="mt-2 text-lg font-medium text-gray-900">Not following anyone</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      When you follow people, they'll appear here.
                    </p>
                  </div>
                ) : (
                  <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {following.map((followedUser) => (
                        <UserCard 
                          key={followedUser._id} 
                          user={followedUser} 
                          showFollowButton 
                          showMessageButton={true}
                          onFollowChange={handleFollowChange}
                        />
                      ))}
                    </div>

                    {hasMore.following && (
                      <div className="mt-6 flex justify-center">
                        <Button onClick={loadMore} variant="light" isLoading={loading.following}>
                          Load More
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Followers;
